// The immersive Git client: a full-editor-area, three-pane source-control
// workspace opened from the activity bar. It REUSES the same views as the
// Source Control sidebar panel (BranchesView | GitGraphView | GitCommitPanel),
// laid out side by side in resizable panels instead of behind tabs. A single
// shared `refreshToken` is bumped whenever any pane mutates the repo (a branch
// switch / create / delete, a checkout, a tag) so the sibling panes re-read, and
// the header toolbar's actions (refresh / stash / pop / fetch / pull / push /
// undo / redo) bump it on completion. All `git` access is guarded because the
// capability is optional (desktop-only); the empty state shows when no
// repository resolves for the active doc or workspace.
//
// The header is the three-zone GitClientToolbar (LEFT workspace switcher, CENTER
// search, RIGHT action cluster); a thin GitStatusStrip sits at the very bottom.
//
// Reflog undo/redo: "Undo last commit" soft-resets HEAD to its first parent
// (keeping the changes staged) and pushes the undone commit onto a redo stack;
// "Redo" soft-resets forward to that commit. `canUndo` is recomputed from
// `git.log(root,{max:1})` (HEAD has a parent) on every refresh; `canRedo`
// mirrors the redo stack into a render bump.
//
// Context menus: the reused views call `useContextMenu`, which is provided
// app-wide by the ContextMenuProvider mounted near the App root, so this client
// does NOT mount its own provider.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { GitStatus, PlatformBridge } from '@slate/core';
import { GitBranch } from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useDocStore } from '../../store/docStore';
import { toast } from '../../store/toastStore';
import { useGitRepo } from '../GitApiContext';
import { GitLeftNav } from './GitLeftNav';
import { GitGraphView, type GitSelection } from './GitGraphView';
import { GitCommitPanel } from './GitCommitPanel';
import { GitWorkspaceSwitcher, type GitWorkspaceEntry } from './GitWorkspaceSwitcher';
import { GitClientToolbar } from './GitClientToolbar';
import { GitStatusStrip } from './GitStatusStrip';

export interface GitClientProps {
  platform: PlatformBridge;
}

export function GitClient({ platform }: GitClientProps): ReactElement {
  // Bumped after any pane's mutation (and by the header actions) so all panes
  // re-read on their next render. Also passed to useGitRepo as a re-resolve
  // nonce so a freshly `init`ed repo becomes selectable immediately.
  const [refreshToken, setRefreshToken] = useState(0);
  const onMutated = useCallback(() => setRefreshToken((v) => v + 1), []);

  const { selectedRoot, selectRoot, git } = useGitRepo(platform, refreshToken);
  const workspaceRoots = useDocStore((s) => s.workspaceRoots);

  // The center graph's selection (a commit, the working tree, or nothing); drives
  // the contextual right commit panel (GitCommitPanel). Reset when the repo
  // changes so a commit from one repo never leaks into another; the reflog redo
  // stack is repo-scoped too, so clear it on the same change.
  const [selection, setSelection] = useState<GitSelection>(null);
  // The branch/tag the graph is scoped to (driven by the left nav's ref clicks
  // and the graph's own <select>). Lifted here so both stay in sync; cleared on
  // repo change so a filter from one repo never carries into another.
  const [filterRev, setFilterRev] = useState<string | undefined>(undefined);
  // Left-nav "jump to ref": reveal a commit in the graph (scroll + select). The
  // token lets the same hash be revealed repeatedly.
  const [reveal, setReveal] = useState<{ hash: string; token: number }>({ hash: '', token: 0 });
  const onReveal = useCallback(
    (hash: string): void => setReveal((r) => ({ hash, token: r.token + 1 })),
    [],
  );
  // Isolate/focus: dim everything outside a ref's or commit's ancestry. Driven by
  // the left nav (focus a branch/tag) and the graph (right-click a commit);
  // cleared on repo/filter change or with Escape (handled in the graph).
  const [isolateHash, setIsolateHash] = useState<string | null>(null);
  // Focus a ref from the left nav: dim others AND reveal (scroll to) the commit,
  // fetching more pages if it is beyond the loaded window.
  const onFocus = useCallback((hash: string): void => {
    setIsolateHash(hash);
    setReveal((r) => ({ hash, token: r.token + 1 }));
  }, []);
  const redoStack = useRef<string[]>([]);
  // A render-only counter: bumped whenever the redo stack changes so `canRedo`
  // (read from the ref) recomputes and the Redo button re-renders.
  const [, bumpRedo] = useState(0);
  const canRedo = redoStack.current.length > 0;
  useEffect(() => {
    setSelection(null);
    setFilterRev(undefined);
    setIsolateHash(null);
    redoStack.current = [];
    bumpRedo((v) => v + 1);
  }, [selectedRoot]);
  // A filter change reloads a different commit set, so any focus/isolate is stale.
  useEffect(() => {
    setIsolateHash(null);
  }, [filterRev]);

  // The repo status (working-tree dirtiness, branch, ahead/behind) plus whether
  // HEAD has a parent (so Undo is offered). Both come from the same refresh:
  // re-read on repo change and after any mutation; superseded async runs drop.
  const [status, setStatus] = useState<GitStatus | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  useEffect(() => {
    if (!selectedRoot || !git) {
      setStatus(null);
      setCanUndo(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const s = await git.status(selectedRoot);
        if (!cancelled) setStatus(s);
      } catch {
        if (!cancelled) setStatus(null);
      }
      try {
        const head = (await git.log(selectedRoot, { max: 1 }))[0];
        if (!cancelled) setCanUndo(!!head && head.parents.length > 0);
      } catch {
        if (!cancelled) setCanUndo(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRoot, git, refreshToken]);

  const workingDirty = (status?.files.length ?? 0) > 0;
  const branchLabel = status?.branch ?? undefined;

  // Resolve every open workspace to its repo toplevel (or null) for the header
  // switcher. Re-resolved on workspace / repo change and after any mutation
  // (notably after Initialize Git, so the brand-new repo appears); superseded
  // async runs are dropped.
  const [workspaces, setWorkspaces] = useState<GitWorkspaceEntry[]>([]);
  useEffect(() => {
    if (!git) {
      setWorkspaces([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const paths = workspaceRoots
        .map((r) => r.path)
        .filter((p): p is string => typeof p === 'string' && p.length > 0);
      const entries = await Promise.all(
        paths.map(async (path): Promise<GitWorkspaceEntry> => {
          try {
            return { path, repoRoot: await git.repoRoot(path) };
          } catch {
            return { path, repoRoot: null };
          }
        }),
      );
      if (!cancelled) setWorkspaces(entries);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceRoots, git, refreshToken]);

  // Initialize a git repo in a non-repo workspace, then re-resolve the list and
  // make the new repo active. Failures surface as a toast.
  const handleInit = useCallback(
    async (path: string): Promise<void> => {
      if (!git) return;
      try {
        const root = await git.init(path);
        onMutated();
        selectRoot(root);
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Failed to initialize Git repository', {
          kind: 'error',
        });
      }
    },
    [git, onMutated, selectRoot],
  );

  // Header search (filters the center graph) and the transient/busy state shared
  // by every async action in the toolbar's right cluster.
  const [search, setSearch] = useState('');
  const [op, setOp] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Run one async git action: flag busy, show its label in the status strip,
  // refresh every pane on success, surface failures (upstream/auth, conflicts,
  // an empty stash) as a toast.
  const run = useCallback(
    async (label: string, fn: () => Promise<void>): Promise<void> => {
      setBusy(true);
      setOp(label);
      try {
        await fn();
        onMutated();
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
      } finally {
        setBusy(false);
        setOp(null);
      }
    },
    [onMutated],
  );

  const onStash = useCallback(() => {
    if (!selectedRoot || !git) return;
    void run('Stashing', () => git.stashPush(selectedRoot, undefined));
  }, [selectedRoot, git, run]);

  const onPop = useCallback(() => {
    if (!selectedRoot || !git) return;
    void run('Popping stash', () => git.stashPop(selectedRoot));
  }, [selectedRoot, git, run]);

  const onFetch = useCallback(() => {
    if (!selectedRoot || !git) return;
    void run('Fetching', () => git.fetch(selectedRoot, { prune: true }));
  }, [selectedRoot, git, run]);

  const onPull = useCallback(() => {
    if (!selectedRoot || !git) return;
    void run('Pulling', () => git.pull(selectedRoot, {}));
  }, [selectedRoot, git, run]);

  const onPush = useCallback(() => {
    if (!selectedRoot || !git) return;
    // Plain push uses the branch's configured upstream; a missing upstream or an
    // auth failure surfaces via the run() toast. Auto-set-upstream is a later
    // refinement (not wired here).
    void run('Pushing', () => git.push(selectedRoot, {}));
  }, [selectedRoot, git, run]);

  const onUndo = useCallback(() => {
    if (!selectedRoot || !git) return;
    void run('Undo', async () => {
      const head = (await git.log(selectedRoot, { max: 1 }))[0];
      if (!head || head.parents.length === 0) throw new Error('Nothing to undo');
      await git.reset(selectedRoot, head.parents[0], 'soft');
      redoStack.current.push(head.hash);
      bumpRedo((v) => v + 1);
    });
  }, [selectedRoot, git, run]);

  const onRedo = useCallback(() => {
    if (!selectedRoot || !git) return;
    void run('Redo', async () => {
      const oid = redoStack.current.pop();
      if (!oid) throw new Error('Nothing to redo');
      await git.reset(selectedRoot, oid, 'soft');
      bumpRedo((v) => v + 1);
    });
  }, [selectedRoot, git, run]);

  return (
    <div className="mp-git-client">
      <GitClientToolbar
        switcher={
          <GitWorkspaceSwitcher
            workspaces={workspaces}
            activeRoot={selectedRoot}
            branchLabel={branchLabel}
            onSelectRepo={selectRoot}
            onInit={handleInit}
          />
        }
        search={search}
        onSearch={setSearch}
        busy={busy}
        onRefresh={onMutated}
        onStash={onStash}
        onPop={onPop}
        onFetch={onFetch}
        onPull={onPull}
        onPush={onPush}
        undo={{ canUndo, canRedo, onUndo, onRedo }}
        disabled={!selectedRoot}
      />

      {!selectedRoot ? (
        <div className="mp-git__empty">
          <GitBranch size={24} aria-hidden className="mp-git__empty-icon" />
          <p className="mp-git__empty-title">No Git repository</p>
          <p className="mp-git__empty-note">
            Open a folder that is a Git repository, or a file inside one, to use source control.
          </p>
        </div>
      ) : (
        <PanelGroup
          direction="horizontal"
          autoSaveId="slate-git-client"
          className="mp-git-client__panes"
        >
          <Panel defaultSize={22} minSize={14}>
            <div className="mp-git-client__pane">
              <GitLeftNav
                root={selectedRoot}
                git={git}
                refreshToken={refreshToken}
                onMutated={onMutated}
                onReveal={onReveal}
                onFocus={onFocus}
              />
            </div>
          </Panel>
          <PanelResizeHandle className="mp-resize-handle" aria-label="Resize branches and history" />
          <Panel defaultSize={53} minSize={30}>
            <div className="mp-git-client__pane">
              <GitGraphView
                root={selectedRoot}
                git={git}
                refreshToken={refreshToken}
                selection={selection}
                onSelect={setSelection}
                onMutated={onMutated}
                workingDirty={workingDirty}
                search={search}
                rev={filterRev}
                onRevChange={setFilterRev}
                revealHash={reveal.hash}
                revealToken={reveal.token}
                isolate={isolateHash}
                onIsolate={setIsolateHash}
              />
            </div>
          </Panel>
          <PanelResizeHandle className="mp-resize-handle" aria-label="Resize history and changes" />
          <Panel defaultSize={25} minSize={16}>
            <div className="mp-git-client__pane mp-git-client__pane--right">
              <GitCommitPanel
                root={selectedRoot}
                git={git}
                selection={selection}
                refreshToken={refreshToken}
                onMutated={onMutated}
                workingDirty={workingDirty}
              />
            </div>
          </Panel>
        </PanelGroup>
      )}

      <GitStatusStrip
        root={selectedRoot}
        branch={branchLabel}
        ahead={status?.ahead}
        behind={status?.behind}
        op={op}
      />
    </div>
  );
}
